import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, message, Alert, Switch } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AppContext';
import FloatingFooter from '../components/utils/FloatingFooter';
import ImageUploader from '../components/utils/ImageUploader';
import { buildQuillModules, QUILL_TEXT_FORMATS } from '../components/utils/quillConfig';

// 工具函数：清理富文本 Quill 的空内容
function cleanQuillEmpty(html) {
  if (!html) return '';
  const text = html.replace(/<(.|\n)*?>/g, '').trim();
  return text.length === 0 ? '' : html;
}

function isQuillContentEmpty(html) {
  if (!html) return true;
  return html.replace(/<(.|\n)*?>/g, '').trim().length === 0;
}

function normalizeImageUrl(value) {
  if (!value) return '';

  if (Array.isArray(value)) return value[0] || '';

  if (typeof value === 'object') {
    if (typeof value.url === 'string') return value.url;
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return normalizeImageUrl(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  return '';
}

const TsAdvContent = () => {
  const [form] = Form.useForm();
  const { token, selectedRole, userInfo } = useAuth();
  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;
  const id = selectedRole?.id || '';
  const API = process.env.REACT_APP_API_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [imageUrls, setImageUrls] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [showOnceTip, setShowOnceTip] = useState(false);
  const watchedValues = Form.useWatch([], form);
  const watchedMapUrl = Form.useWatch('mapUrl', form);
  const mapPreviewUrl = normalizeImageUrl(watchedMapUrl);

  const quillModules = buildQuillModules([
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
  ]);

  const quillFormats = [
    ...QUILL_TEXT_FORMATS,
    'bold',
    'italic',
    'underline',
    'list',
    'bullet',
    'align',
  ];

  // 一次性顶部提醒
  useEffect(() => {
    if (!localStorage.getItem('hasSeenAdvContentHint')) {
      setShowOnceTip(true);
    }
  }, []);
  const handleCloseTip = () => {
    setShowOnceTip(false);
    localStorage.setItem('hasSeenAdvContentHint', 'true');
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(`${API}/ts/tsadv/${id}/get-content`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Content not initialized for this advertisement.');
        const data = await res.json();
        form.setFieldsValue({
          ...data,
          mapUrl: normalizeImageUrl(data.mapUrl),
          imageUrls: Array.isArray(data.imageUrls) ? data.imageUrls : [],
        });
        const normalizedImageUrls = Array.isArray(data.imageUrls) ? data.imageUrls : [];
        const normalizedShowBanner = (data.searchTags || '').includes('logo');
        setImageUrls(normalizedImageUrls);
        setShowBanner(normalizedShowBanner);
        setInitialSnapshot({
          ...data,
          mapUrl: normalizeImageUrl(data.mapUrl),
          imageUrls: normalizedImageUrls,
          searchTags: normalizedShowBanner ? 'logo' : '',
        });
      } catch (err) {
        setError(err.message || 'Failed to load content.');
      } finally {
        setLoading(false);
      }
    };
    if (id && token) fetchContent();
  }, [id, token, API, form]);

  // 富文本输入长度限制（2048）
  const checkCharacterCount = (event, value) => {
    const text = (value || '').replace(/<(.|\n)*?>/g, '').trim();
    if (text.length >= 2048 && event.key !== "Backspace") {
      event.preventDefault();
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 过滤富文本“空内容”
      const leftDescription = cleanQuillEmpty(values.leftDescription);
      const rightDescription = cleanQuillEmpty(values.rightDescription);
      const summary = (values.description || '').trim();

      // 业务校验（防止只用 validateFields 跳过未输入内容时的校验）
      if (isQuillContentEmpty(leftDescription) && isQuillContentEmpty(rightDescription) && !summary) {
        message.error('Short Description is required if both Left and Right Description are empty.');
        return;
      }
      // 长度校验
      const fieldsToCheck = [
        { name: 'Short Description', value: summary },
        { name: 'Left Description', value: (leftDescription || '').replace(/<(.|\n)*?>/g, '').trim() },
        { name: 'Right Description', value: (rightDescription || '').replace(/<(.|\n)*?>/g, '').trim() }
      ];
      const overLimit = fieldsToCheck.find(f => f.value.length > 2048);
      if (overLimit) {
        message.error(`${overLimit.name} exceeds 2048 characters!`);
        return;
      }

      // 组织 payload
      const searchTags = showBanner ? 'logo' : '';
      const payload = {
        ...values,
        description: summary,
        leftDescription,
        rightDescription,
        mapUrl: normalizeImageUrl(values.mapUrl),
        imageUrls: imageUrls,
        searchTags,
      };
      if (!Array.isArray(payload.imageUrls)) payload.imageUrls = [];

      const res = await fetch(`${API}/ts/tsadv/${id}/update-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update content');
      setInitialSnapshot(payload);
      message.success('Content updated successfully!');
    } catch (err) {
      message.error('Failed to save content.');
    }
  };

  const hasChanges = useMemo(() => {
    if (!initialSnapshot) return false;
    const currentSnapshot = {
      ...(watchedValues || {}),
      mapUrl: normalizeImageUrl(watchedValues?.mapUrl),
      imageUrls,
      searchTags: showBanner ? 'logo' : '',
    };
    return JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot);
  }, [watchedValues, imageUrls, showBanner, initialSnapshot]);

  return (
    <div className="min-h-screen bg-gray-100 pb-24">
      <div className="max-w-7xl w-[92%] mx-auto pt-8 overflow-hidden">
        <h1 className="text-[1.8rem] font-semibold text-blue-600 mb-4">Advertisement Content Management</h1>
        <div className="bg-white p-10 rounded-2xl shadow space-y-10">
          {showOnceTip && (
            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
              <span className="text-blue-700">
                <b>Short Description</b> is <b>required</b> only if both Left and Right Description are empty.<br />
                (Short Description does <b>not support formatting</b>, only plain text will be saved.)
              </span>
              <button
                onClick={handleCloseTip}
                className="ml-4 px-4 py-1 bg-blue-500 text-white hover:bg-blue-600 rounded"
              >
                Got it
              </button>
            </div>
          )}

          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : error ? (
            <Alert type="warning" message={error} showIcon />
          ) : (
            <Form form={form} layout="vertical">
              {/* Title */}
              <Form.Item
                label={<span className="text-xl font-semibold">Title</span>}
                name="name"
                rules={[{ required: true, message: 'Title is required' }]}
              >
                <Input placeholder="Enter title..." />
              </Form.Item>

              {/* Short Description */}
              <Form.Item
                label={
                  <span className="text-xl font-semibold">
                    Short Description <span className="text-xs text-gray-400">(plain text only)</span>
                  </span>
                }
                name="description"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const left = getFieldValue('leftDescription');
                      const right = getFieldValue('rightDescription');
                      const isLeftEmpty = !left || left.replace(/<(.|\n)*?>/g, '').trim().length === 0;
                      const isRightEmpty = !right || right.replace(/<(.|\n)*?>/g, '').trim().length === 0;
                      const isSummaryEmpty = !value || value.trim().length === 0;
                      if (isLeftEmpty && isRightEmpty && isSummaryEmpty) {
                        return Promise.reject(new Error('Short Description is required if both Left and Right Description are empty.'));
                      }
                      if ((value || '').trim().length > 2048) {
                        return Promise.reject(new Error('Max 2048 characters allowed'));
                      }
                      return Promise.resolve();
                    }
                  })
                ]}
                className="mb-8"
              >
                <Input.TextArea
                  rows={4}
                  maxLength={2048}
                  placeholder="If both Left and Right Description are empty, please provide a summary."
                  showCount
                />
                <div className="text-xs text-gray-400 mt-1">
                  <b>Note:</b> Short Description does not support formatting. Only plain text will be saved and displayed.
                </div>
              </Form.Item>

              {/* 富文本编辑器 */}
              <div className="grid grid-cols-2 gap-10">
                <Form.Item
                  label={<span className="text-xl font-semibold">Left Description</span>}
                  name="leftDescription"
                  getValueFromEvent={v => v}
                  className="mb-8"
                  rules={[
                    {
                      validator: (_, value) => {
                        const plainText = (value || '').replace(/<(.|\n)*?>/g, '').trim();
                        if (plainText.length > 2048) {
                          return Promise.reject(new Error('Max 2048 characters allowed'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    className="border min-h-[150px] max-h-[200px] overflow-y-auto"
                    onKeyDown={e => checkCharacterCount(e, form.getFieldValue('leftDescription'))}
                  />
                </Form.Item>

                <Form.Item
                  label={<span className="text-xl font-semibold">Right Description</span>}
                  name="rightDescription"
                  getValueFromEvent={v => v}
                  className="mb-8"
                  rules={[
                    {
                      validator: (_, value) => {
                        const plainText = (value || '').replace(/<(.|\n)*?>/g, '').trim();
                        if (plainText.length > 2048) {
                          return Promise.reject(new Error('Max 2048 characters allowed'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    className="border min-h-[150px] max-h-[200px] overflow-y-auto"
                    onKeyDown={e => checkCharacterCount(e, form.getFieldValue('rightDescription'))}
                  />
                </Form.Item>
              </div>

              {/* Map + Banner */}
              <div className="grid grid-cols-2 gap-10">
                <div>
                  <label className="block text-xl font-semibold mb-2">Map Image (Location)</label>
                  {mapPreviewUrl && (
                    <div className="aspect-video w-full rounded-xl mb-2 border overflow-hidden">
                      <img
                        src={mapPreviewUrl}
                        alt="Map"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <ImageUploader
                    token={token}
                    ownerType="adv"
                    ownerId={id}
                    type="map"
                    prefix={prefix}
                    thumbPrefix={thumbPrefix}
                    width={1200}
                    height={900}
                    onChange={(url) => form.setFieldValue('mapUrl', normalizeImageUrl(url))}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xl font-semibold">Banner Image (Logo)</label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-base">Show Logo</span>
                      <Switch checked={showBanner} onChange={setShowBanner} />
                    </div>
                  </div>
                  {showBanner && (
                    <>
                      {form.getFieldValue('bannerImage') && (
                        <img
                          src={form.getFieldValue('bannerImage')}
                          alt="Banner"
                          className="w-full rounded-xl mb-2 border object-contain max-h-[180px]"
                        />
                      )}
                      <Form.Item
                        name="bannerImage"
                        rules={[{ required: true, message: 'Please upload banner image' }]}
                      >
                        <ImageUploader
                          token={token}
                          ownerType="adv"
                          ownerId={id}
                          type="banner"
                          width={1200}
                          height={900}
                          prefix={prefix}
                          thumbPrefix={thumbPrefix}
                          onChange={(url) => form.setFieldValue('bannerImage', url)}
                        />
                      </Form.Item>
                    </>
                  )}
                </div>
              </div>

              {/* imageUrls 渲染 */}
              <div className="mt-10">
                <label className="text-xl font-semibold block mb-2">Image Carousel</label>
                {imageUrls.length > 0 && (
                  <div className="space-y-6 mb-6">
                    {imageUrls.map((url, index) => (
                      <div
                        key={url}
                        className="grid grid-cols-[1fr_7rem_7rem] items-center gap-4 bg-white border border-gray-300 rounded-lg shadow px-4 py-2"
                      >
                        <div className="aspect-[16/5] w-[50rem] bg-gray-50 rounded overflow-hidden">
                          <img src={url} alt={`${index}`} className="w-full h-full object-contain" />
                        </div>

                        <div className="flex flex-col items-center space-y-2">
                          <button
                            disabled={index === 0}
                            onClick={() => {
                              const newList = [...imageUrls];
                              [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
                              setImageUrls(newList);
                              form.setFieldValue('imageUrls', newList);
                            }}
                            className="text-white text-md border bg-blue-400 rounded w-full px-2 py-1 hover:bg-blue-700 disabled:opacity-30"
                          >
                            Up
                          </button>
                          <button
                            disabled={index === imageUrls.length - 1}
                            onClick={() => {
                              const newList = [...imageUrls];
                              [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
                              setImageUrls(newList);
                              form.setFieldValue('imageUrls', newList);
                            }}
                            className="text-white text-md border bg-blue-400 rounded w-full px-2 py-1 hover:bg-blue-700 disabled:opacity-30"
                          >
                            Down
                          </button>
                        </div>

                        <div className="flex justify-end">
                          <button
                            onClick={() => {
                              const newList = imageUrls.filter((_, i) => i !== index);
                              setImageUrls(newList);
                              form.setFieldValue('imageUrls', newList);
                            }}
                            className="text-sm text-gray-600 rounded px-4 py-1 bg-white hover:text-white hover:bg-red-500 hover:border-red-500 transition"
                          >
                            <DeleteOutlined style={{ fontSize: '1.5rem' }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <ImageUploader
                  token={token}
                  ownerType="adv"
                  ownerId={id}
                  type="content"
                  width={1080}
                  height={340}
                  prefix={prefix}
                  thumbPrefix={thumbPrefix}
                  onChange={(newUrls) => {
                    const urlsToAdd = Array.isArray(newUrls) ? newUrls : [newUrls];
                    const merged = [...imageUrls, ...urlsToAdd].filter(Boolean);
                    setImageUrls(merged);
                    form.setFieldValue('imageUrls', merged);
                  }}
                />
              </div>
            </Form>
          )}
        </div>
      </div>
      <FloatingFooter hasChanges={hasChanges} onSave={handleSave} saveText="Save Content" />
    </div>
  );
};

export default TsAdvContent;
